import React, { useState, useEffect } from 'react';
import xService from '../services/xService';

const XSearchForm = ({ initialData, isEditing, onSave, onCancel }) => {
  const [activeTab, setActiveTab] = useState('wizard');
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    description: initialData?.description || '',
    query: initialData?.query || '',
    max_results: initialData?.max_results || 10,
    time_constraint_value: initialData?.time_constraint_value || 1,
    time_constraint_unit: initialData?.time_constraint_unit || 'hours',
    is_active: initialData?.is_active !== undefined ? initialData.is_active : true,
    filter_group_id: initialData?.filter_group_id || null
  });
  
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);

  // Wizard form state
  const [wizardData, setWizardData] = useState({
    // Checkboxes for flags
    hasLinks: false,
    noLinks: false,
    isRetweet: false,
    isNotRetweet: false,
    isReply: false,
    isNotReply: false,
    englishLanguage: false,
    hasHashtags: false,
    noHashtags: false,
    hasMedia: false,
    noMedia: false,
    hasImages: false,
    noImages: false,
    hasVideos: false,
    noVideos: false,
    isVerified: false,
    isNotVerified: false,
    // Text inputs for AND terms
    andTerms: [''],
    orGroups: [['']], // Array of OR groups, each group is an array of terms
    notTerms: ['']
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);

  // Parse raw query into wizard data
  const parseQueryToWizard = (query) => {
    if (!query || !query.trim()) {
      return {
        hasLinks: false,
        noLinks: false,
        isRetweet: false,
        isNotRetweet: false,
        isReply: false,
        isNotReply: false,
        englishLanguage: false,
        hasHashtags: false,
        noHashtags: false,
        hasMedia: false,
        noMedia: false,
        hasImages: false,
        noImages: false,
        hasVideos: false,
        noVideos: false,
        isVerified: false,
        isNotVerified: false,
        andTerms: [''],
        orGroups: [['']],
        notTerms: ['']
      };
    }

    // Normalize whitespace - replace ALL whitespace characters (including newlines, tabs, etc.) with single space
    const normalizedQuery = query
      .replace(/\r\n/g, ' ')  // Handle Windows line endings first
      .replace(/[\r\n\t\f\v]/g, ' ')  // Handle all other whitespace characters
      .replace(/\s+/g, ' ')  // Collapse multiple spaces
      .trim();

    const parsed = {
      hasLinks: false,
      noLinks: false,
      isRetweet: false,
      isNotRetweet: false,
      isReply: false,
      isNotReply: false,
      englishLanguage: false,
      hasHashtags: false,
      noHashtags: false,
      hasMedia: false,
      noMedia: false,
      hasImages: false,
      noImages: false,
      hasVideos: false,
      noVideos: false,
      isVerified: false,
      isNotVerified: false,
      andTerms: [],
      orGroups: [], // Array of OR groups
      notTerms: []
    };

    // Split query into tokens, handling quoted phrases
    const tokens = [];
    let current = '';
    let inQuotes = false;
    let inParens = false;
    let parenContent = '';

    for (let i = 0; i < normalizedQuery.length; i++) {
      const char = normalizedQuery[i];

      if (char === '"') {
        inQuotes = !inQuotes;
        current += char;
        if (!inQuotes) {
          current = ""; // Keep quotes together
        }
      } else if (char === '(' && !inQuotes) {
        inParens = true;
        parenContent = '';
      } else if (char === ')' && !inQuotes && inParens) {
        inParens = false;
        // Parse OR terms inside parentheses
        const orTerms = parenContent
          .split(/\s+OR\s+/i)
          .map(term => {
            // Aggressively clean whitespace and quotes
            let cleaned = term.replace(/[\r\n\t\f\v]/g, ' ').replace(/\s+/g, ' ').trim();
            // Remove surrounding quotes if they exist
            if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
              cleaned = cleaned.slice(1, -1).trim();
            }
            return cleaned;
          })
          .filter(term => {
            // Filter out empty terms and operator terms
            if (!term || !term.trim()) return false;
            const lower = term.toLowerCase().trim();
            return !lower.startsWith('has:') &&
              !lower.startsWith('is:') &&
              !lower.startsWith('lang:');
          });
        if (orTerms.length > 0) {
          parsed.orGroups.push(orTerms);
        }
        parenContent = '';
      } else if (inParens) {
        parenContent += char;
      } else if (char === ' ' && !inQuotes) {
        if (current.trim()) {
          tokens.push(current.trim());
          current = '';
        }
      } else {
        current += char;
      }
    }
    if (current.trim()) {
      tokens.push(current.trim());
    }

    // Process tokens
    for (const token of tokens) {
      const lower = token.toLowerCase();

      // Check for flags (positive)
      if (lower === 'has:links') {
        parsed.hasLinks = true;
      } else if (lower === 'is:retweet') {
        parsed.isRetweet = true;
      } else if (lower === 'is:reply') {
        parsed.isReply = true;
      } else if (lower === 'lang:en') {
        parsed.englishLanguage = true;
      } else if (lower === 'has:hashtags') {
        parsed.hasHashtags = true;
      } else if (lower === 'has:media') {
        parsed.hasMedia = true;
      } else if (lower === 'has:images') {
        parsed.hasImages = true;
      } else if (lower === 'has:videos') {
        parsed.hasVideos = true;
      } else if (lower === 'is:verified') {
        parsed.isVerified = true;
      }
      // Check for negative flags
      else if (lower === '-has:links') {
        parsed.noLinks = true;
      } else if (lower === '-is:retweet') {
        parsed.isNotRetweet = true;
      } else if (lower === '-is:reply') {
        parsed.isNotReply = true;
      } else if (lower === '-has:hashtags') {
        parsed.noHashtags = true;
      } else if (lower === '-has:media') {
        parsed.noMedia = true;
      } else if (lower === '-has:images') {
        parsed.noImages = true;
      } else if (lower === '-has:videos') {
        parsed.noVideos = true;
      } else if (lower === '-is:verified') {
        parsed.isNotVerified = true;
      }
      // Check for NOT terms (but not if it's a flag we already checked)
      else if (token.startsWith('-') &&
        !lower.startsWith('-has:') &&
        !lower.startsWith('-is:') &&
        !lower.startsWith('-lang:')) {
        const notTerm = token.substring(1).replace(/^"|"$/g, '');
        if (notTerm) {
          parsed.notTerms.push(notTerm);
        }
      }
      // Regular AND terms (not in OR group)
      else if (!token.includes('(') && !token.includes(')') &&
        !lower.startsWith('has:') &&
        !lower.startsWith('is:') &&
        !lower.startsWith('lang:')) {
        const cleanTerm = token.replace(/^"|"$/g, '');
        if (cleanTerm && cleanTerm.toUpperCase() !== 'OR' && cleanTerm.toUpperCase() !== 'AND') {
          parsed.andTerms.push(cleanTerm);
        }
      }
    }

    // Ensure at least one empty field for each array
    if (parsed.andTerms.length === 0) parsed.andTerms = [''];
    if (parsed.orGroups.length === 0) parsed.orGroups = [['']];
    if (parsed.notTerms.length === 0) parsed.notTerms = [''];

    return parsed;
  };

  // Initialize wizard data from initial query on mount
  useEffect(() => {
    if (!isInitialized && initialData?.query) {
      const parsedWizard = parseQueryToWizard(initialData.query);
      setWizardData(parsedWizard);
      setIsInitialized(true);
    }
  }, [initialData?.query, isInitialized]);
  
  // Fetch groups on mount
  useEffect(() => {
    const fetchGroups = async () => {
      setLoadingGroups(true);
      try {
        const data = await xService.getAllGroups();
        setGroups(data?.groups || []);
      } catch (err) {
        console.error('Failed to fetch groups:', err);
        setGroups([]);
      } finally {
        setLoadingGroups(false);
      }
    };
    
    fetchGroups();
  }, []);

  // Build query from wizard data
  const buildQueryFromWizard = () => {
    const parts = [];

    const formatWizardTerm = (term) => {
      const cleanTerm = term.replace(/[\r\n\t\f\v]/g, ' ').replace(/\s+/g, ' ').trim();
      if (!cleanTerm) return '';

      const isNegated = cleanTerm.startsWith('-');
      const body = isNegated ? cleanTerm.substring(1).trim() : cleanTerm;
      if (!body) return '';

      const quotedBody = body.startsWith('"') ? body : `"${body}"`;
      return isNegated ? `-${quotedBody}` : quotedBody;
    };

    // Add OR groups first
    wizardData.orGroups.forEach(group => {
      const validOrTerms = group.filter(term => term && term.trim());
      if (validOrTerms.length > 0) {
        const orClause = validOrTerms
          .map(term => formatWizardTerm(term))
          .filter(term => term && term.trim()) // Remove any empty terms
          .join(' OR ');
        if (orClause) {
          parts.push(`(${orClause})`);
        }
      }
    });

    // Add AND terms
    const validAndTerms = wizardData.andTerms.filter(term => term && term.trim());
    if (validAndTerms.length > 0) {
      const andClause = validAndTerms
        .map(term => formatWizardTerm(term))
        .filter(term => term && term.trim()) // Remove any empty terms
        .join(' ');
      if (andClause) {
        parts.push(andClause);
      }
    }

    // Add NOT terms
    const validNotTerms = wizardData.notTerms.filter(term => term && term.trim());
    if (validNotTerms.length > 0) {
      validNotTerms.forEach(term => {
        const cleanTerm = term.trim();
        if (cleanTerm) {
          // Always add quotes unless already quoted
          const notTerm = !cleanTerm.startsWith('"')
            ? `"${cleanTerm}"`
            : cleanTerm;
          parts.push(`-${notTerm}`);
        }
      });
    }

    // Add flags
    if (wizardData.hasLinks) parts.push('has:links');
    if (wizardData.noLinks) parts.push('-has:links');
    if (wizardData.isRetweet) parts.push('is:retweet');
    if (wizardData.isNotRetweet) parts.push('-is:retweet');
    if (wizardData.isReply) parts.push('is:reply');
    if (wizardData.isNotReply) parts.push('-is:reply');
    if (wizardData.englishLanguage) parts.push('lang:en');
    if (wizardData.hasHashtags) parts.push('has:hashtags');
    if (wizardData.noHashtags) parts.push('-has:hashtags');
    if (wizardData.hasMedia) parts.push('has:media');
    if (wizardData.noMedia) parts.push('-has:media');
    if (wizardData.hasImages) parts.push('has:images');
    if (wizardData.noImages) parts.push('-has:images');
    if (wizardData.hasVideos) parts.push('has:videos');
    if (wizardData.noVideos) parts.push('-has:videos');
    if (wizardData.isVerified) parts.push('is:verified');
    if (wizardData.isNotVerified) parts.push('-is:verified');

    // Filter out empty parts and join with single space, then normalize spacing
    const query = parts.filter(p => p && p.trim()).join(' ');
    // Clean up any multiple spaces that might have been created
    return query.replace(/\s+/g, ' ').trim();
  };

  // Update query when wizard data changes (only in wizard tab)
  useEffect(() => {
    if (activeTab === 'wizard' && isInitialized) {
      const newQuery = buildQueryFromWizard();
      setFormData(prev => ({
        ...prev,
        query: newQuery
      }));
    }
  }, [wizardData, activeTab, isInitialized]);

  // Parse raw query to wizard when switching to wizard tab
  const handleTabChange = (tab) => {
    if (tab === 'wizard' && activeTab === 'raw') {
      // Parse the current raw query into wizard format
      const parsedWizard = parseQueryToWizard(formData.query);
      setWizardData(parsedWizard);
    } else if (tab === 'raw' && activeTab === 'wizard') {
      // Update query from wizard when switching to raw
      const newQuery = buildQueryFromWizard();
      setFormData(prev => ({
        ...prev,
        query: newQuery
      }));
    }
    setActiveTab(tab);
  };

  const handleWizardCheckboxChange = (field) => {
    setWizardData(prev => {
      const newValue = !prev[field];
      const updates = { [field]: newValue };

      // Handle mutually exclusive pairs - uncheck the opposite when checking one
      if (newValue) {
        const exclusivePairs = {
          hasLinks: 'noLinks',
          noLinks: 'hasLinks',
          isRetweet: 'isNotRetweet',
          isNotRetweet: 'isRetweet',
          isReply: 'isNotReply',
          isNotReply: 'isReply',
          hasHashtags: 'noHashtags',
          noHashtags: 'hasHashtags',
          hasMedia: 'noMedia',
          noMedia: 'hasMedia',
          hasImages: 'noImages',
          noImages: 'hasImages',
          hasVideos: 'noVideos',
          noVideos: 'hasVideos',
          isVerified: 'isNotVerified',
          isNotVerified: 'isVerified'
        };

        const oppositeField = exclusivePairs[field];
        if (oppositeField) {
          updates[oppositeField] = false;
        }
      }

      return {
        ...prev,
        ...updates
      };
    });
  };

  const handleWizardArrayChange = (field, index, value) => {
    setWizardData(prev => {
      const newArray = [...prev[field]];
      const currentValue = newArray[index];

      // Preserve the "-" prefix if it was there
      if (currentValue.startsWith('-')) {
        newArray[index] = `-${value}`;
      } else {
        newArray[index] = value;
      }

      return {
        ...prev,
        [field]: newArray
      };
    });
  };

  const toggleNotInAndTerm = (index) => {
    setWizardData(prev => {
      const newArray = [...prev.andTerms];
      const term = newArray[index];

      // Toggle the "-" prefix
      if (term.startsWith('-')) {
        newArray[index] = term.substring(1);
      } else {
        newArray[index] = `-${term}`;
      }

      return {
        ...prev,
        andTerms: newArray
      };
    });
  };

  const handleOrGroupTermChange = (groupIndex, termIndex, value) => {
    setWizardData(prev => {
      const newGroups = [...prev.orGroups];
      newGroups[groupIndex] = [...newGroups[groupIndex]];
      const currentTerm = newGroups[groupIndex][termIndex];

      // Preserve the "-" prefix if it was there
      if (currentTerm.startsWith('-')) {
        newGroups[groupIndex][termIndex] = `-${value}`;
      } else {
        newGroups[groupIndex][termIndex] = value;
      }

      return {
        ...prev,
        orGroups: newGroups
      };
    });
  };

  const addWizardArrayItem = (field) => {
    setWizardData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const addOrGroup = () => {
    setWizardData(prev => ({
      ...prev,
      orGroups: [...prev.orGroups, ['']]
    }));
  };

  const addOrTermToGroup = (groupIndex) => {
    setWizardData(prev => {
      const newGroups = [...prev.orGroups];
      newGroups[groupIndex] = [...newGroups[groupIndex], ''];
      return {
        ...prev,
        orGroups: newGroups
      };
    });
  };

  const removeWizardArrayItem = (field, index) => {
    setWizardData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const removeOrGroup = (groupIndex) => {
    setWizardData(prev => ({
      ...prev,
      orGroups: prev.orGroups.filter((_, i) => i !== groupIndex)
    }));
  };

  const removeOrTermFromGroup = (groupIndex, termIndex) => {
    setWizardData(prev => {
      const newGroups = [...prev.orGroups];
      newGroups[groupIndex] = newGroups[groupIndex].filter((_, i) => i !== termIndex);
      // If group is empty, remove it
      if (newGroups[groupIndex].length === 0) {
        return {
          ...prev,
          orGroups: newGroups.filter((_, i) => i !== groupIndex)
        };
      }
      return {
        ...prev,
        orGroups: newGroups
      };
    });
  };

  const toggleNotInOrTerm = (groupIndex, termIndex) => {
    setWizardData(prev => {
      const newGroups = [...prev.orGroups];
      newGroups[groupIndex] = [...newGroups[groupIndex]];
      const term = newGroups[groupIndex][termIndex];

      // Toggle the "-" prefix
      if (term.startsWith('-')) {
        // Remove the "-" prefix
        newGroups[groupIndex][termIndex] = term.substring(1);
      } else {
        // Add the "-" prefix
        newGroups[groupIndex][termIndex] = `-${term}`;
      }

      return {
        ...prev,
        orGroups: newGroups
      };
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === 'checkbox' ? checked : value;

    // Convert empty string to null for filter_group_id
    if (name === 'filter_group_id' && value === '') {
      newValue = null;
    }

    // Enforce max 7 days for time constraint
    if (name === 'time_constraint_value' && formData.time_constraint_unit === 'days') {
      newValue = Math.min(parseInt(newValue) || 0, 7);
    } else if (name === 'time_constraint_unit' && value === 'days' && formData.time_constraint_value > 7) {
      setFormData(prev => ({
        ...prev,
        time_constraint_value: 7,
        time_constraint_unit: value
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name.trim()) {
      setError('Search name is required');
      return;
    }
    if (!formData.query.trim() && !formData.filter_group_id) {
      setError('Search query is required unless you select a group filter');
      return;
    }

    try {
      setSaving(true);
      await onSave(formData);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to save search');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="bi bi-exclamation-circle me-2"></i>
          {error}
        </div>
      )}

      <div className="mb-3">
        <label htmlFor="name" className="form-label">
          Search Name <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          className="form-control"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="e.g., Tech News Today"
          maxLength={100}
          required
        />
        <div className="form-text">
          A descriptive name for this search configuration
        </div>
      </div>

      <div className="mb-3">
        <label htmlFor="description" className="form-label">
          Description
        </label>
        <textarea
          className="form-control"
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="e.g., Monitor tech industry news and AI developments"
          rows={2}
          maxLength={500}
        />
        <div className="form-text">
          Optional description to help identify the purpose of this search
        </div>
      </div>

      <div className="mb-3">
        <label htmlFor="filter_group_id" className="form-label">
          Filter by User Group
        </label>
        <select
          className="form-select"
          id="filter_group_id"
          name="filter_group_id"
          value={formData.filter_group_id || ''}
          onChange={handleChange}
          disabled={loadingGroups}
        >
          <option value="">No group filter (search all users)</option>
          {groups.map(group => (
            <option key={group.id} value={group.id}>
              {group.name} ({group.x_user_ids?.length || 0} users)
            </option>
          ))}
        </select>
        <div className="form-text">
          Optional: Filter tweets to only those from users in a specific group. 
          The query will automatically be split if needed to handle large groups.
          If a group is selected, you can leave the query blank to fetch everything posted by that group.
        </div>
      </div>

      <div className="mb-3">
        <label className="form-label">
          Search Query Builder
        </label>

        {/* Tab Navigation */}
        <ul className="nav nav-tabs mb-3" role="tablist">
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === 'wizard' ? 'active' : ''}`}
              type="button"
              onClick={() => handleTabChange('wizard')}
            >
              <i className="bi bi-magic me-2"></i>
              Wizard
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === 'raw' ? 'active' : ''}`}
              type="button"
              onClick={() => handleTabChange('raw')}
            >
              <i className="bi bi-code-slash me-2"></i>
              Raw Query
            </button>
          </li>
        </ul>

        {/* Tab Content */}
        <div className="tab-content">
          {/* Wizard Tab */}
          {activeTab === 'wizard' && (
            <div className="card">
              <div className="card-body">
                {/* Search Terms Section */}
                <div className="mb-4">
                  <h6 className="text-primary mb-3">
                    <i className="bi bi-search me-2"></i>
                    Search Terms
                  </h6>

                  {/* AND Terms */}
                  <div className="mb-3">
                    <label className="form-label fw-bold">Must contain (AND)</label>
                    <div className="row" style={{ flexWrap: 'wrap' }}>
                      {wizardData.andTerms.map((term, index) => (
                        <React.Fragment key={index}>
                          <div className="col-md-4 mb-2">
                            <div className="input-group input-group-sm" style={{ flexWrap: 'wrap' }}>
                              <button
                                type="button"
                                className={`btn ${term.startsWith('-') ? 'btn-danger' : 'btn-outline-secondary'} btn-sm`}
                                onClick={() => toggleNotInAndTerm(index)}
                                title="Toggle NOT (excludes this term)"
                                style={{ flexShrink: 0 }}
                              >
                                NOT
                              </button>
                              <input
                                type="text"
                                className="form-control form-control-sm text-truncate"
                                value={term.startsWith('-') ? term.substring(1) : term}
                                onChange={(e) => handleWizardArrayChange('andTerms', index, e.target.value)}
                                placeholder="Enter keyword"
                                style={{ minWidth: '0', maxWidth: '100%' }}
                                title={term.startsWith('-') ? term.substring(1) : term}
                              />
                              {wizardData.andTerms.length > 1 && (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => removeWizardArrayItem('andTerms', index)}
                                  style={{ flexShrink: 0 }}
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              )}
                            </div>
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary mt-2"
                      onClick={() => addWizardArrayItem('andTerms')}
                    >
                      <i className="bi bi-plus-circle me-1"></i>
                      Add AND term
                    </button>
                  </div>

                  {/* OR Groups */}
                  <div className="mb-3">
                    <label className="form-label fw-bold">May contain (OR Groups)</label>
                    <div className="text-muted small mb-2">
                      Each box is an OR group (term1 OR term2 OR term3). Multiple groups are combined with AND.
                    </div>

                    {wizardData.orGroups.map((group, groupIndex) => (
                      <div key={groupIndex} className="card mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <small className="text-muted fw-bold">OR Group {groupIndex + 1}</small>
                            {wizardData.orGroups.length > 1 && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => removeOrGroup(groupIndex)}
                                title="Remove this OR group"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            )}
                          </div>

                          <div className="row" style={{ flexWrap: 'wrap' }}>
                            {group.map((term, termIndex) => (
                              <React.Fragment key={termIndex}>
                                <div className="col-md-4 mb-2">
                                  <div className="input-group input-group-sm" style={{ flexWrap: 'wrap' }}>
                                    <button
                                      type="button"
                                      className={`btn ${term.startsWith('-') ? 'btn-danger' : 'btn-outline-secondary'} btn-sm`}
                                      onClick={() => toggleNotInOrTerm(groupIndex, termIndex)}
                                      title="Toggle NOT (excludes this term)"
                                      style={{ flexShrink: 0 }}
                                    >
                                      NOT
                                    </button>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm text-truncate"
                                      value={term.startsWith('-') ? term.substring(1) : term}
                                      onChange={(e) => handleOrGroupTermChange(groupIndex, termIndex, e.target.value)}
                                      placeholder="Enter keyword"
                                      style={{ minWidth: '0', maxWidth: '100%' }}
                                      title={term.startsWith('-') ? term.substring(1) : term}
                                    />
                                    {group.length > 1 && (
                                      <button
                                        type="button"
                                        className="btn btn-outline-danger btn-sm"
                                        onClick={() => removeOrTermFromGroup(groupIndex, termIndex)}
                                        style={{ flexShrink: 0 }}
                                      >
                                        <i className="bi bi-trash"></i>
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </React.Fragment>
                            ))}
                          </div>

                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => addOrTermToGroup(groupIndex)}
                          >
                            <i className="bi bi-plus-circle me-1"></i>
                            Add OR term to this group
                          </button>
                        </div>
                      </div>
                    ))}

                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={addOrGroup}
                    >
                      <i className="bi bi-plus-square me-1"></i>
                      Add new OR group (parentheses)
                    </button>
                  </div>
                </div>

                <hr />

                {/* Filters Section */}
                <div className="mb-3">
                  <h6 className="text-primary mb-3">
                    <i className="bi bi-funnel me-2"></i>
                    Filters
                  </h6>

                  <div className="row">
                    <div className="col-md-3">
                      <label className="form-label fw-bold small text-muted">LINKS</label>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="wizard-hasLinks"
                          checked={wizardData.hasLinks}
                          onChange={() => handleWizardCheckboxChange('hasLinks')}
                        />
                        <label className="form-check-label" htmlFor="wizard-hasLinks">
                          Has links
                        </label>
                      </div>
                      <div className="form-check mb-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="wizard-noLinks"
                          checked={wizardData.noLinks}
                          onChange={() => handleWizardCheckboxChange('noLinks')}
                        />
                        <label className="form-check-label" htmlFor="wizard-noLinks">
                          No links
                        </label>
                      </div>

                      <label className="form-label fw-bold small text-muted">RETWEETS</label>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="wizard-isRetweet"
                          checked={wizardData.isRetweet}
                          onChange={() => handleWizardCheckboxChange('isRetweet')}
                        />
                        <label className="form-check-label" htmlFor="wizard-isRetweet">
                          Is retweet
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="wizard-isNotRetweet"
                          checked={wizardData.isNotRetweet}
                          onChange={() => handleWizardCheckboxChange('isNotRetweet')}
                        />
                        <label className="form-check-label" htmlFor="wizard-isNotRetweet">
                          Is not retweet
                        </label>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <label className="form-label fw-bold small text-muted">REPLIES</label>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="wizard-isReply"
                          checked={wizardData.isReply}
                          onChange={() => handleWizardCheckboxChange('isReply')}
                        />
                        <label className="form-check-label" htmlFor="wizard-isReply">
                          Is reply
                        </label>
                      </div>
                      <div className="form-check mb-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="wizard-isNotReply"
                          checked={wizardData.isNotReply}
                          onChange={() => handleWizardCheckboxChange('isNotReply')}
                        />
                        <label className="form-check-label" htmlFor="wizard-isNotReply">
                          Is not reply
                        </label>
                      </div>

                      <label className="form-label fw-bold small text-muted">HASHTAGS</label>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="wizard-hasHashtags"
                          checked={wizardData.hasHashtags}
                          onChange={() => handleWizardCheckboxChange('hasHashtags')}
                        />
                        <label className="form-check-label" htmlFor="wizard-hasHashtags">
                          Has hashtags
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="wizard-noHashtags"
                          checked={wizardData.noHashtags}
                          onChange={() => handleWizardCheckboxChange('noHashtags')}
                        />
                        <label className="form-check-label" htmlFor="wizard-noHashtags">
                          No hashtags
                        </label>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <label className="form-label fw-bold small text-muted">MEDIA</label>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="wizard-hasMedia"
                          checked={wizardData.hasMedia}
                          onChange={() => handleWizardCheckboxChange('hasMedia')}
                        />
                        <label className="form-check-label" htmlFor="wizard-hasMedia">
                          Has media
                        </label>
                      </div>
                      <div className="form-check mb-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="wizard-noMedia"
                          checked={wizardData.noMedia}
                          onChange={() => handleWizardCheckboxChange('noMedia')}
                        />
                        <label className="form-check-label" htmlFor="wizard-noMedia">
                          No media
                        </label>
                      </div>

                      <label className="form-label fw-bold small text-muted">IMAGES</label>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="wizard-hasImages"
                          checked={wizardData.hasImages}
                          onChange={() => handleWizardCheckboxChange('hasImages')}
                        />
                        <label className="form-check-label" htmlFor="wizard-hasImages">
                          Has images
                        </label>
                      </div>
                      <div className="form-check mb-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="wizard-noImages"
                          checked={wizardData.noImages}
                          onChange={() => handleWizardCheckboxChange('noImages')}
                        />
                        <label className="form-check-label" htmlFor="wizard-noImages">
                          No images
                        </label>
                      </div>

                      <label className="form-label fw-bold small text-muted">VIDEOS</label>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="wizard-hasVideos"
                          checked={wizardData.hasVideos}
                          onChange={() => handleWizardCheckboxChange('hasVideos')}
                        />
                        <label className="form-check-label" htmlFor="wizard-hasVideos">
                          Has videos
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="wizard-noVideos"
                          checked={wizardData.noVideos}
                          onChange={() => handleWizardCheckboxChange('noVideos')}
                        />
                        <label className="form-check-label" htmlFor="wizard-noVideos">
                          No videos
                        </label>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <label className="form-label fw-bold small text-muted">VERIFICATION</label>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="wizard-isVerified"
                          checked={wizardData.isVerified}
                          onChange={() => handleWizardCheckboxChange('isVerified')}
                        />
                        <label className="form-check-label" htmlFor="wizard-isVerified">
                          From verified accounts
                        </label>
                      </div>
                      <div className="form-check mb-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="wizard-isNotVerified"
                          checked={wizardData.isNotVerified}
                          onChange={() => handleWizardCheckboxChange('isNotVerified')}
                        />
                        <label className="form-check-label" htmlFor="wizard-isNotVerified">
                          Not from verified accounts
                        </label>
                      </div>

                      <label className="form-label fw-bold small text-muted">LANGUAGE</label>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="wizard-englishLanguage"
                          checked={wizardData.englishLanguage}
                          onChange={() => handleWizardCheckboxChange('englishLanguage')}
                        />
                        <label className="form-check-label" htmlFor="wizard-englishLanguage">
                          English language
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Generated Query Preview */}
                <div className="alert alert-info mb-0">
                  <small className="text-muted d-block mb-1">Generated Query:</small>
                  <code>{formData.query || '(no query yet)'}</code>
                </div>
              </div>
            </div>
          )}

          {/* Raw Query Tab */}
          {activeTab === 'raw' && (
            <div>
              <textarea
                className="form-control"
                id="query"
                name="query"
                value={formData.query}
                onChange={handleChange}
                placeholder="Optional with group filter. e.g., AI OR artificial intelligence -bot"
                rows={5}
                maxLength={512}
              />
              <div className="form-text">
                X search query using{' '}
                <a
                  href="https://developer.twitter.com/en/docs/twitter-api/tweets/search/integrate/build-a-query"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  X search operators
                </a>
                . Use OR, AND, NOT, quotes for phrases, etc.
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mb-3">
        <div className="row g-3">
          <div className="col-md-4">
            <label htmlFor="max_results" className="form-label">
              Max Results
            </label>
            <input
              type="number"
              className="form-control"
              id="max_results"
              name="max_results"
              value={formData.max_results}
              onChange={handleChange}
              min={10}
              max={100}
            />
            <div className="form-text">
              Number of tweets (10-100)
            </div>
          </div>

          <div className="col-md-8">
            <label className="form-label">
              Time Constraint
            </label>
            <div className="d-flex align-items-center gap-2">
              <span>Last</span>
              <input
                type="number"
                className="form-control"
                id="time_constraint_value"
                name="time_constraint_value"
                value={formData.time_constraint_value}
                onChange={handleChange}
                placeholder="Number (1-100)"
                min={1}
                max={formData.time_constraint_unit === 'days' ? 7 : 100}
                style={{ flex: '1' }}
              />
              <select
                className="form-select"
                id="time_constraint_unit"
                name="time_constraint_unit"
                value={formData.time_constraint_unit}
                onChange={handleChange}
                style={{ flex: '1' }}
              >
                <option value="minutes">Minutes</option>
                <option value="hours">Hours</option>
                <option value="days">Days</option>
              </select>
            </div>
            <div className="form-text">
              Optional time range filter for Manual runs. Schedule run will always search up till the last scheduled run.
            </div>
          </div>
        </div>
      </div>

      {isEditing && (
        <div className="mb-3 form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id="is_active"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
          />
          <label className="form-check-label" htmlFor="is_active">
            Active
          </label>
          <div className="form-text">
            Inactive searches cannot be run
          </div>
        </div>
      )}

      <div className="d-flex justify-content-end gap-2">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={saving}
        >
          {saving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Saving...
            </>
          ) : (
            <>
              <i className="bi bi-check-circle me-2"></i>
              {isEditing ? 'Update' : 'Create'}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default XSearchForm;
